import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Subtle parchment ribbon shown on /angels to highlight the newly-released
 * artwork. Auto-dismisses on click, persists in localStorage so it never
 * re-appears for the same browser. Also self-expires after `expiresAt`.
 *
 * Bumping ANNOUNCEMENT_KEY effectively resets the dismissal for every user,
 * so future "what's new" moments (e.g., angels 052-060 launch) can re-use
 * this component by bumping the version suffix.
 */
const ANNOUNCEMENT_KEY = "angelsquad.whatsnew.v1-artwork-2026-02";
// 30 days after the artwork ship date.
const EXPIRES_AT = new Date("2026-06-23T00:00:00Z").getTime();

export default function WhatsNewBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (Date.now() >= EXPIRES_AT) return;
        try {
            if (localStorage.getItem(ANNOUNCEMENT_KEY) === "dismissed") return;
        } catch {
            // localStorage may be unavailable (private mode) — just show once.
        }
        setVisible(true);
    }, []);

    const dismiss = () => {
        try {
            localStorage.setItem(ANNOUNCEMENT_KEY, "dismissed");
        } catch {
            // noop
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            className="mb-8 flex items-start gap-3 rounded-md border border-[#cfa052]/30 bg-[#1a1612]/70 px-4 py-3 backdrop-blur-sm"
            data-testid="whats-new-banner"
        >
            <span
                className="mt-0.5 font-label text-[10px] tracking-[0.3em] text-[#cfa052] uppercase shrink-0"
                aria-hidden="true"
            >
                New
            </span>
            <p className="flex-1 font-poetic text-[#e8e4db]/90 text-sm md:text-base leading-relaxed">
                Fresh artwork just arrived for the Sixty.{" "}
                <span className="text-[#9a958a]">
                    Open any Angel and toggle to <span className="text-[#cfa052]">Original Sketch</span> to see how each one began.
                </span>
            </p>
            <button
                type="button"
                onClick={dismiss}
                className="shrink-0 -mr-1 p-1 text-[#9a958a] hover:text-[#e8e4db] transition-colors"
                aria-label="Dismiss"
                data-testid="whats-new-dismiss"
            >
                <X size={16} />
            </button>
        </div>
    );
}
